import { useMutation } from 'react-query';
import { axios } from '@/lib/axios';
import { MutationConfig } from '@/lib/react-query';
import { FormField, SaveFormField } from '../types';
import { showNotification } from '@/utils/notificationUtils';
import { Business } from '@/features/business';

type UseBasicFieldsOptions = {
  config?: MutationConfig<typeof getBasicFields>;
};
export const getBasicFields = (): Promise<any> => {
  return axios.get(`/intake-form/owner/fields`);
};

export const useGetBasicFields = ({ config }: UseBasicFieldsOptions = {}) => {
  return useMutation({
    onSuccess: () => {},
    ...config,
    mutationFn: getBasicFields,
  });
};

type UsePetFieldsOptions = {
  config?: MutationConfig<typeof getBasicFields>;
};
export const getPetFields = (): Promise<any> => {
  return axios.get(`/intake-form/pet/fields`);
};

export const useGetPetFields = ({ config }: UsePetFieldsOptions = {}) => {
  return useMutation({
    onSuccess: () => {},
    ...config,
    mutationFn: getPetFields,
  });
};

export type CreateFieldData = {
  data?: FormField;
};
export const createFormFields = ({ data }: CreateFieldData): Promise<FormField> => {
  const req = { ...data };
  const fieldId = data?.id;
  delete req.id;
  delete req.fieldName;
  return axios.post(
    fieldId ? `intake-form/fields/${fieldId}/update` : `intake-form/fields/create`,
    req
  );
};

type UseFormFieldOptions = {
  config?: MutationConfig<typeof createFormFields>;
};

export const useCreateFormFields = ({ config }: UseFormFieldOptions = {}) => {
  return useMutation({
    onSuccess: (res, data) => {
      showNotification({
        type: 'success',
        title: data?.data?.id
          ? 'Form field updated successfully'
          : 'Form field created successfully',
      });
    },
    ...config,
    mutationFn: createFormFields,
  });
};

export type DeleteFieldData = {
  data?: FormField;
};
export const deleteFormFields = ({ data }: DeleteFieldData): Promise<FormField> => {
  return axios.delete(`intake-form/fields/${data?.id}/delete`);
};

type UseDeleteFormFieldOptions = {
  config?: MutationConfig<typeof deleteFormFields>;
};

export const useDeleteFormFields = ({ config }: UseDeleteFormFieldOptions = {}) => {
  return useMutation({
    onSuccess: (res, data) => {
      showNotification({
        type: 'success',
        title: 'Form field deleted successfully',
      });
    },
    ...config,
    mutationFn: deleteFormFields,
  });
};

export type SaveFormFieldData = {
  formTitle: string;
  formDescription: string;
  fields: SaveFormField[] | FormField[];
};

export const saveFormFields = ({ formTitle, formDescription, fields }: SaveFormFieldData) => {
  return axios.post(`intake-form/save`, { formTitle, formDescription, fields });
};

type UseSaveFormFieldOptions = {
  config?: MutationConfig<typeof saveFormFields>;
};

export const useSaveFormFields = ({ config }: UseSaveFormFieldOptions = {}) => {
  return useMutation({
    onSuccess: () => {
      showNotification({
        type: 'success',
        title: 'Form saved successfully',
      });
    },
    ...config,
    mutationFn: saveFormFields,
  });
};

type IntakeFormReq = {
  slug?: string;
};

export type MyFormFieldsResponse = {
  form: SaveFormFieldData;
  business: Business;
};
export const getMyIntakeForm = ({
  slug,
}: IntakeFormReq): Promise<MyFormFieldsResponse | SaveFormFieldData> => {
  return axios.get(slug ? `intake-form/public/${slug}/get` : `intake-form/get`);
};

type UseGetMyIntakeForm = {
  config?: MutationConfig<typeof getMyIntakeForm>;
};

export const useGetMyIntakeForm = ({ config }: UseGetMyIntakeForm = {}) => {
  return useMutation({
    ...config,
    mutationFn: getMyIntakeForm,
  });
};
export const deleteMyIntakeForm = (): Promise<any> => {
  return axios.delete(`intake-form/delete`);
};

type UseDeleteMyIntakeForm = {
  config?: MutationConfig<typeof getMyIntakeForm>;
};

export const useDeleteMyIntakeForm = ({ config }: UseDeleteMyIntakeForm = {}) => {
  return useMutation({
    ...config,
    mutationFn: deleteMyIntakeForm,
  });
};

export type CustomerFormData = { [key: string]: string };
export type SaveCustomerData = {
  request: {
    owner: CustomerFormData;
    pets: CustomerFormData[];
  };
  slug: string;
};

export const saveCustomerResponse = (data: SaveCustomerData) => {
  return axios.post(`intake-form/${data.slug}/response/save`, data.request);
};

type UseSaveCustomerResponseOptions = {
  config?: MutationConfig<typeof saveCustomerResponse>;
};

export const useSaveCustomerResponse = ({ config }: UseSaveCustomerResponseOptions = {}) => {
  return useMutation({
    onSuccess: () => {
      showNotification({
        type: 'success',
        title: 'Details saved successfully',
      });
    },
    ...config,
    mutationFn: saveCustomerResponse,
  });
};

export type requestDTOUploadImage = {
  file: FormData;
};

export type UploadFileResponse = {
  fileUrl: string;
  fileName: string;
  fileSize: string;
};

export const uploadFile = ({ file }: requestDTOUploadImage): Promise<UploadFileResponse> => {
  return axios.post('/intake-form/upload-document', file);
};

type UseUpdateBusinessOptions1 = {
  config?: MutationConfig<typeof uploadFile>;
};

export const useUploadFile = ({ config }: UseUpdateBusinessOptions1 = {}) => {
  return useMutation({
    onSuccess: () => {},
    ...config,
    mutationFn: uploadFile,
  });
};

type CustomerSubmissionsReq = {
  customerId?: string;
  page: number;
  perPage: number;
};

export const getCustomerSubmissions = ({
  customerId,
  page,
  perPage,
}: CustomerSubmissionsReq): Promise<any> => {
  return axios.get(
    `/intake-form/customer/${customerId}/responses/get?sort=createdAt&order=desc&perPage=${perPage}&page=${page}`
  );
};

type UseGetCustomerSubmissions = {
  config?: MutationConfig<typeof getCustomerSubmissions>;
};

export const useGetCustomerSubmissions = ({ config }: UseGetCustomerSubmissions = {}) => {
  return useMutation({
    ...config,
    mutationFn: getCustomerSubmissions,
  });
};

type MakeFormPrimaryReq = {
  responseId: string;
};

export const makeCustomerResponsePrimary = ({ responseId }: MakeFormPrimaryReq): Promise<any> => {
  return axios.post(`/intake-form/${responseId}/primary`);
};

type UseMakeCustomerResponsePrimary = {
  config?: MutationConfig<typeof makeCustomerResponsePrimary>;
};

export const useMakeCustomerResponsePrimary = ({ config }: UseMakeCustomerResponsePrimary = {}) => {
  return useMutation({
    onSuccess: () => {
      showNotification({
        type: 'success',
        title: 'Selected form response as set as primary successfully',
      });
    },
    ...config,
    mutationFn: makeCustomerResponsePrimary,
  });
};

//api to get form details GET
//api to get response details GET
type GetIntakeFormReq = {
  formId?: string;
};

export const getIntakeFormFieldsById = ({
  formId,
}: GetIntakeFormReq): Promise<SaveFormFieldData> => {
  return axios.get(`intake-form/${formId}/get`);
};

type UseGetIntakeFormFieldsById = {
  config?: MutationConfig<typeof getIntakeFormFieldsById>;
};

export const useGetIntakeFormFieldsById = ({ config }: UseGetIntakeFormFieldsById = {}) => {
  return useMutation({
    ...config,
    mutationFn: getIntakeFormFieldsById,
  });
};

type GetCustomerSubmissionRequest = {
  responseId?: string;
};

export const getCustomerSubmissionById = ({
  responseId,
}: GetCustomerSubmissionRequest): Promise<any> => {
  return axios.get(`/intake-form/response/${responseId}/get`);
};

type UseGetCustomerSubmissionById = {
  config?: MutationConfig<typeof getCustomerSubmissionById>;
};

export const useGetCustomerSubmissionById = ({ config }: UseGetCustomerSubmissionById = {}) => {
  return useMutation({
    ...config,
    mutationFn: getCustomerSubmissionById,
  });
};
